import type { PropsWithChildren } from 'react';

import type { DataAttributesProps } from '../../utils/types';
import type { IconName } from '../icons';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { cva } from 'class-variance-authority';

import { Icon } from '../icons';
import { Typography } from '../typography';

export const progressStepStatuses = [
  'complete',
  'waiting',
  'idle',
  'locked',
  'archived',
  'error',
] as const;

export type ProgressStepStatus = (typeof progressStepStatuses)[number];

type CaptionVariant = 'default' | 'error';

export type ProgressStepperCaption = { text: string; variant: CaptionVariant };

type ProgressStepProps = DataAttributesProps & {
  caption?: ProgressStepperCaption;
  disabled?: boolean;
  label: string;
  status: ProgressStepStatus;
};

export type ProgressStepperProps = DataAttributesProps & {
  steps: ProgressStepProps[];
};

export const statusIconMap: Record<ProgressStepStatus, IconName> = {
  complete: 'circle-check',
  waiting: 'hourglass',
  idle: 'dot',
  locked: 'lock',
  archived: 'archive',
  error: 'circle-x',
};

const stepCircle = cva('flex size-8 items-center justify-center rounded-full', {
  variants: {
    status: {
      complete:
        'bg-semantic-success-background text-semantic-success-foreground',
      waiting:
        'bg-semantic-warning-background text-semantic-warning-foreground',
      idle: 'bg-semantic-neutral-background text-muted-foreground',
      locked: 'bg-muted text-muted-foreground',
      archived: 'bg-muted text-muted-foreground',
      error: 'bg-semantic-error-background text-semantic-error-foreground',
    },
    disabled: { true: 'opacity-60' },
  },
});

export const toConnectorTone = (rightStatus: ProgressStepStatus) =>
  ['locked', 'idle'].includes(rightStatus) ? 'neutral' : 'default';

const connectorStyle = cva('h-px w-full flex-1 self-center rounded', {
  variants: {
    tone: {
      neutral: 'bg-semantic-neutral-background',
      default: 'bg-muted-foreground/55',
    },
  },
});

const textLineStyle = cva('block whitespace-nowrap font-medium', {
  variants: {
    tone: {
      default: 'text-foreground',
      error: 'text-semantic-error-foreground',
    },
    disabled: {
      true: 'text-muted-foreground',
    },
  },
  compoundVariants: [
    {
      tone: 'error',
      disabled: true,
      class: 'text-semantic-error-foreground/60',
    },
  ],
});

const ProgressStepItem = ({
  caption,
  disabled,
  isLast,
  label,
  nextStatus,
  status,
}: ProgressStepItemProps) => {
  const icon = statusIconMap[status];

  return (
    <div
      aria-disabled={disabled || undefined}
      className={cn('relative flex min-w-0 items-center gap-2', {
        'flex-1': !isLast,
      })}
      data-disabled={disabled ? '' : undefined}
      data-status={status}
      role="listitem"
    >
      <div
        className={stepCircle({
          status,
          disabled,
        })}
      >
        <Icon name={icon} size="sm" />
      </div>

      {!isLast && !!nextStatus && (
        <div
          aria-hidden
          className={connectorStyle({ tone: toConnectorTone(nextStatus) })}
        />
      )}

      <div className="absolute left-4 top-9 flex size-8 w-max -translate-x-1/2 flex-col items-center text-center">
        <Typography
          as="span"
          className={textLineStyle({ tone: 'default', disabled })}
          variant="caption"
        >
          {label}
        </Typography>

        {!!caption && (
          <Typography
            as="span"
            className={textLineStyle({ tone: caption.variant, disabled })}
            variant="caption"
          >
            {caption.text}
          </Typography>
        )}
      </div>
    </div>
  );
};

export const ProgressStepper = ({
  'data-testid': dataTestId,
  steps,
}: ProgressStepperProps) => (
  <div className="flex items-center gap-2" data-testid={dataTestId} role="list">
    {steps.map((step, idx) => (
      <ProgressStepItem
        key={`${step.label}-${idx}`}
        {...step}
        isLast={idx === steps.length - 1}
        nextStatus={steps[idx + 1]?.status}
      />
    ))}
  </div>
);

type ProgressStepItemProps = PropsWithChildren<
  ProgressStepProps & {
    isLast: boolean;
    nextStatus?: ProgressStepStatus;
  }
>;
